/**
 * Translation key map used by the whitelabel onboarding flow.
 *
 * Every string the onboarding flow renders is read from this map so that
 * tenants can supply their own keys (and therefore their own locale-file
 * namespace) without component code having to know about specific brands.
 *
 * Default values for Barilla are kept here so that components can fall back
 * to them when no whitelabel onboarding config is active.
 */
export type OnboardingTranslationKeys = {
  // PageLayoutAuth / NavigationBar titles
  pageTitleNextSteps: string;
  pageTitleCreateFarm: string;
  pageTitleAddFields: string;
  pageTitleAssignCrop: string;
  pageTitleEnroll: string;

  // Next Steps
  nextStepsCreateFarmTitle: string;
  nextStepsCreateFarmSubTitle: string;
  nextStepsAddFieldsTitle: string;
  nextStepsAddFieldsSubTitle: string;
  nextStepsAssignCropTitle: string;
  nextStepsAssignCropSubTitle: string;
  nextStepsEnrollFarmTitle: string;
  nextStepsEnrollFarmSubTitle: string;
  nextStepsContinue: string;

  // Create Farm - Onboarding
  createFarmEnterFarmDetails: string;
  createFarmFarmName: string;
  createFarmFarmNamePlaceholder: string;
  createFarmOrganization: string;
  createFarmAddress: string;
  createFarmZipCode: string;
  createFarmCity: string;
  createFarmFarmNameMandatory: string;

  // Create Farm - Modal
  createFarmModalFarmName: string;
  createFarmModalLegalEntityName: string;
  createFarmModalBillingAddress: string;
  createFarmModalShippingAddress: string;
  createFarmModalSameAsShipping: string;
  createFarmModalPostalCode: string;
  createFarmModalProvince: string;
  createFarmModalCountry: string;

  // Add Fields
  addFieldsHeaderTitle: string;
  addFieldsHeaderSubTitle: string;

  // Assign Crop
  assignCropHeaderTitle: string;
  assignCropHeaderSubTitle: string;
  assignCropTitle: string;
  assignCropDescription: string;

  // Enroll Intro
  enrollIntroHeaderTitle: string;
  enrollIntroHeaderSubTitle?: string;
  enrollIntroOverline?: string;
  enrollIntroTitle: string;
  enrollIntroDescription: string;
  enrollIntroEnrollNow: string;

  // Enroll Select Fields
  enrollSelectFieldsTitle: string;
  enrollSelectFieldsSearchPlaceholder: string;
  enrollSelectFieldsAddFields: string;

  // Success Modal
  successModalTitle: string;
  successModalSubTitle: string;
};

const BARILLA_NS = 'component.BarillaOnboardingPage';
const BARILLA_MODAL_NS = 'component.barilla.createFarmModal';
const BARILLA_SUCCESS_NS = 'component.BarillaOnboardingSuccessModal';

export const BARILLA_ONBOARDING_TRANSLATION_KEYS: OnboardingTranslationKeys = {
  // Page titles
  pageTitleNextSteps: `${BARILLA_NS}.step.nextSteps.title`,
  pageTitleCreateFarm: `${BARILLA_NS}.step.createFarm.title`,
  pageTitleAddFields: `${BARILLA_NS}.step.addFields.title`,
  pageTitleAssignCrop: `${BARILLA_NS}.step.assignCrop.title`,
  pageTitleEnroll: `${BARILLA_NS}.step.enroll.title`,

  // Next Steps
  nextStepsCreateFarmTitle: `${BARILLA_NS}.step.nextSteps.createFarm.title`,
  nextStepsCreateFarmSubTitle: `${BARILLA_NS}.step.nextSteps.createFarm.subTitle`,
  nextStepsAddFieldsTitle: `${BARILLA_NS}.step.nextSteps.addFields.title`,
  nextStepsAddFieldsSubTitle: `${BARILLA_NS}.step.nextSteps.addFields.subTitle`,
  nextStepsAssignCropTitle: `${BARILLA_NS}.step.nextSteps.assignCrop.title`,
  nextStepsAssignCropSubTitle: `${BARILLA_NS}.step.nextSteps.assignCrop.subTitle`,
  nextStepsEnrollFarmTitle: `${BARILLA_NS}.step.nextSteps.enrollFarm.title`,
  nextStepsEnrollFarmSubTitle: `${BARILLA_NS}.step.nextSteps.enrollFarm.subTitle`,
  nextStepsContinue: `${BARILLA_NS}.step.nextSteps.continue`,

  // Create Farm - Onboarding
  createFarmEnterFarmDetails: `${BARILLA_NS}.step.createFarm.enterFarmDetails`,
  createFarmFarmName: `${BARILLA_NS}.step.createFarm.farmName`,
  createFarmFarmNamePlaceholder: `${BARILLA_NS}.step.createFarm.farmName.placeholder`,
  createFarmOrganization: `${BARILLA_NS}.step.createFarm.organization`,
  createFarmAddress: `${BARILLA_NS}.step.createFarm.address`,
  createFarmZipCode: `${BARILLA_NS}.step.createFarm.zipCode`,
  createFarmCity: `${BARILLA_NS}.step.createFarm.city`,
  createFarmFarmNameMandatory: `${BARILLA_NS}.step.createFarm.farmName.mandatory`,

  // Create Farm - Modal
  createFarmModalFarmName: `${BARILLA_MODAL_NS}.farmName`,
  createFarmModalLegalEntityName: `${BARILLA_MODAL_NS}.legalEntityName`,
  createFarmModalBillingAddress: `${BARILLA_MODAL_NS}.billingAddress`,
  createFarmModalSameAsShipping: `${BARILLA_MODAL_NS}.sameAsShipping`,
  createFarmModalShippingAddress: `${BARILLA_MODAL_NS}.shippingAddress`,
  createFarmModalPostalCode: `${BARILLA_MODAL_NS}.postalCode`,
  createFarmModalProvince: `${BARILLA_MODAL_NS}.province`,
  createFarmModalCountry: `${BARILLA_MODAL_NS}.country`,

  // Add Fields
  addFieldsHeaderTitle: `${BARILLA_NS}.step.addFields.header.title`,
  addFieldsHeaderSubTitle: `${BARILLA_NS}.step.addFields.header.subTitle`,

  // Assign Crop
  assignCropHeaderTitle: `${BARILLA_NS}.step.assignCrop.header.title`,
  assignCropHeaderSubTitle: `${BARILLA_NS}.step.assignCrop.header.subTitle`,
  assignCropTitle: `${BARILLA_NS}.step.assignCrop.content.title`,
  assignCropDescription: `${BARILLA_NS}.step.assignCrop.content.description`,

  // Enroll Intro
  enrollIntroHeaderTitle: `${BARILLA_NS}.step.enroll.intro.header.title`,
  enrollIntroHeaderSubTitle: `${BARILLA_NS}.step.enroll.intro.header.subTitle`,
  enrollIntroOverline: `${BARILLA_NS}.step.enroll.intro.content.overline`,
  enrollIntroTitle: `${BARILLA_NS}.step.enroll.intro.content.title`,
  enrollIntroDescription: `${BARILLA_NS}.step.enroll.intro.content.description`,
  enrollIntroEnrollNow: `${BARILLA_NS}.step.enroll.intro.enrollNow`,

  // Enroll Select Fields
  enrollSelectFieldsTitle: `${BARILLA_NS}.step.enroll.selectFields.title`,
  enrollSelectFieldsSearchPlaceholder: `${BARILLA_NS}.step.enroll.selectFields.search.placeholder`,
  enrollSelectFieldsAddFields: `${BARILLA_NS}.step.enroll.selectFields.addFields`,

  // Success Modal
  successModalTitle: `${BARILLA_SUCCESS_NS}.title`,
  successModalSubTitle: `${BARILLA_SUCCESS_NS}.subTitle`,
};

const PERONI_NS = 'component.PeroniOnboardingPage';
const PERONI_MODAL_NS = 'component.peroni.createFarmModal';
const PERONI_SUCCESS_NS = 'component.PeroniOnboardingSuccessModal';

export const PERONI_ONBOARDING_TRANSLATION_KEYS: OnboardingTranslationKeys = {
  pageTitleNextSteps: `${PERONI_NS}.step.nextSteps.title`,
  pageTitleCreateFarm: `${PERONI_NS}.step.createFarm.title`,
  pageTitleAddFields: `${PERONI_NS}.step.addFields.title`,
  pageTitleAssignCrop: `${PERONI_NS}.step.assignCrop.title`,
  pageTitleEnroll: `${PERONI_NS}.step.enroll.title`,

  nextStepsCreateFarmTitle: `${PERONI_NS}.step.nextSteps.createFarm.title`,
  nextStepsCreateFarmSubTitle: `${PERONI_NS}.step.nextSteps.createFarm.subTitle`,
  nextStepsAddFieldsTitle: `${PERONI_NS}.step.nextSteps.addFields.title`,
  nextStepsAddFieldsSubTitle: `${PERONI_NS}.step.nextSteps.addFields.subTitle`,
  nextStepsAssignCropTitle: `${PERONI_NS}.step.nextSteps.assignCrop.title`,
  nextStepsAssignCropSubTitle: `${PERONI_NS}.step.nextSteps.assignCrop.subTitle`,
  nextStepsEnrollFarmTitle: `${PERONI_NS}.step.nextSteps.enrollFarm.title`,
  nextStepsEnrollFarmSubTitle: `${PERONI_NS}.step.nextSteps.enrollFarm.subTitle`,
  nextStepsContinue: `${PERONI_NS}.step.nextSteps.continue`,

  createFarmEnterFarmDetails: `${PERONI_NS}.step.createFarm.enterFarmDetails`,
  createFarmFarmName: `${PERONI_NS}.step.createFarm.farmName`,
  createFarmFarmNamePlaceholder: `${PERONI_NS}.step.createFarm.farmName.placeholder`,
  createFarmOrganization: `${PERONI_NS}.step.createFarm.organization`,
  createFarmAddress: `${PERONI_NS}.step.createFarm.address`,
  createFarmZipCode: `${PERONI_NS}.step.createFarm.zipCode`,
  createFarmCity: `${PERONI_NS}.step.createFarm.city`,
  createFarmFarmNameMandatory: `${PERONI_NS}.step.createFarm.farmName.mandatory`,

  createFarmModalFarmName: `${PERONI_MODAL_NS}.farmName`,
  createFarmModalLegalEntityName: `${PERONI_MODAL_NS}.legalEntityName`,
  createFarmModalBillingAddress: `${PERONI_MODAL_NS}.billingAddress`,
  createFarmModalSameAsShipping: `${PERONI_MODAL_NS}.sameAsShipping`,
  createFarmModalShippingAddress: `${PERONI_MODAL_NS}.shippingAddress`,
  createFarmModalPostalCode: `${PERONI_MODAL_NS}.postalCode`,
  createFarmModalProvince: `${PERONI_MODAL_NS}.province`,
  createFarmModalCountry: `${PERONI_MODAL_NS}.country`,

  addFieldsHeaderTitle: `${PERONI_NS}.step.addFields.header.title`,
  addFieldsHeaderSubTitle: `${PERONI_NS}.step.addFields.header.subTitle`,

  assignCropHeaderTitle: `${PERONI_NS}.step.assignCrop.header.title`,
  assignCropHeaderSubTitle: `${PERONI_NS}.step.assignCrop.header.subTitle`,
  assignCropTitle: `${PERONI_NS}.step.assignCrop.content.title`,
  assignCropDescription: `${PERONI_NS}.step.assignCrop.content.description`,

  enrollIntroHeaderTitle: `${PERONI_NS}.step.enroll.intro.header.title`,

  enrollIntroTitle: `${PERONI_NS}.step.enroll.intro.content.title`,
  enrollIntroDescription: `${PERONI_NS}.step.enroll.intro.content.description`,
  enrollIntroEnrollNow: `${PERONI_NS}.step.enroll.intro.enrollNow`,

  enrollSelectFieldsTitle: `${PERONI_NS}.step.enroll.selectFields.title`,
  enrollSelectFieldsSearchPlaceholder: `${PERONI_NS}.step.enroll.selectFields.search.placeholder`,
  enrollSelectFieldsAddFields: `${PERONI_NS}.step.enroll.selectFields.addFields`,

  successModalTitle: `${PERONI_SUCCESS_NS}.title`,
  successModalSubTitle: `${PERONI_SUCCESS_NS}.subTitle`,
};
